/**
 * Estados e blocos de layout da pré-análise eletrônica (E78.F3).
 *
 * Mesmo recorte de `MesaEletronicaStates`: erro, sucesso, carregando e vazio
 * saem da página para que ela seja composição. Sem isso, as ramificações
 * somadas às da tabela estouram o limite de complexidade do lint — que aqui
 * está certo, porque a página passa a ser difícil de ler.
 */
import { useState } from 'react';

import { CcbDoCasoNaMesa } from '@/components/ExameEletronico/CcbDaMesa';
import { SeloEmAtualizacao } from '@/components/ExameEletronico/EmAtualizacao';
import { ROTULOS_DO_RESUMO } from '@/components/ExameEletronico/HashComOProtocolo';
import { PecasParadasPelaCustodia } from '@/components/ExameEletronico/PecasParadasPelaCustodia';
import { TabelaDePecas, type TabelaDePecasProps } from '@/components/ExameEletronico/TabelaDePecas';
import { Blueprint, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  CcbDaMesaData,
  ExameEletronicoCasoData,
  ObjetoBloqueadoData,
} from '@/lib/exame_eletronico';
import { paradasDoCaso } from '@/lib/exame_eletronico_custodia';
import { contagemDoConfronto, resumoDoConfronto } from '@/lib/exame_eletronico_hash';
import {
  FONTES_CONSULTADAS,
  type EstadoDaConsulta,
  type FonteConsultada,
  type ResumoDasConsultas,
} from '@/lib/exame_eletronico_processamento';

const t = ptBR.exameEletronico;
const tp = ptBR.exameEletronico.tabelaDePecas;
const ta = ptBR.exameEletronico.processamentoAutomatico;

/** A frase de uma fonte num estado; `null` quando não há o que dizer. */
function fraseDaConsulta(fonte: FonteConsultada, estado: EstadoDaConsulta): string {
  if (estado === 'nao_apurada') return ta.consultaNaoApurada;
  return ta.consultas[fonte][estado];
}

/**
 * O que as consultas externas fizeram no processamento (E91.F3, GUS-51 RF09).
 *
 * "Processamento concluído" é uma coisa; "as fontes foram consultadas" era outra
 * — e saía mesmo com o bureau sem chave (UXD-09). O que ficou sem consulta tem
 * bloco PRÓPRIO, de atenção, fora da faixa de sucesso (E91.F3.C02,
 * F-E91.F3-02): dentro dela, "Consulta cadastral indisponível" herdava o tom de
 * concluído e quem lê de relance não notava. O estado de cada fonte fica sob
 * demanda, em tom neutro. Nunca o `detalhe` do servidor.
 */
export function ConsultasDoProcessamento({
  consultas,
}: {
  consultas: ResumoDasConsultas;
}): JSX.Element {
  const [aberto, setAberto] = useState(false);
  return (
    <>
      {consultas.pendentes.length > 0 ? (
        <div
          className="mt-sc-2 border border-warning bg-warning-50 px-sc-3 py-sc-2 text-body-sm text-warning-700"
          role="status"
          data-testid="processamento-consultas-pendentes"
        >
          <p className="font-semibold">{ta.consultasPendentesTitulo}</p>
          <ul className="mt-1 list-disc pl-sc-4">
            {consultas.pendentes.map((fonte) => (
              <li
                key={fonte}
                data-testid={`consulta-${fonte}`}
                data-estado={consultas.porFonte[fonte]}
              >
                {fraseDaConsulta(fonte, consultas.porFonte[fonte])}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="mt-sc-1 text-body-sm text-fg-muted" data-testid="processamento-consultas">
        <Button
          type="button"
          variant="ghost"
          className="px-0 text-body-sm"
          aria-expanded={aberto}
          onClick={() => setAberto((atual) => !atual)}
          data-testid="processamento-consultas-alternar"
        >
          {aberto ? ta.consultasOcultar : ta.consultasDetalhe}
        </Button>
        {aberto ? (
          <ul className="list-disc pl-sc-4" data-testid="processamento-consultas-detalhe">
            {FONTES_CONSULTADAS.map((fonte) => (
              <li
                key={fonte}
                data-testid={`consulta-detalhe-${fonte}`}
                data-estado={consultas.porFonte[fonte]}
              >
                {fraseDaConsulta(fonte, consultas.porFonte[fonte])}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </>
  );
}

export function PreAnaliseAvisos({
  erroGlobal,
  sucessoGlobal,
  consultas = null,
  caso,
}: {
  erroGlobal: string | null;
  sucessoGlobal: string | null;
  consultas?: ResumoDasConsultas | null;
  caso: ExameEletronicoCasoData | null;
}): JSX.Element {
  return (
    <>
      {erroGlobal !== null ? (
        <p
          className="mt-sc-4 border border-danger bg-danger-50 px-sc-3 py-sc-2 text-body-sm text-danger-700"
          role="alert"
          data-testid="pre-analise-error"
        >
          {erroGlobal}
        </p>
      ) : null}
      {sucessoGlobal !== null ? (
        <div
          className="mt-sc-4 border border-success/30 bg-success-50 px-sc-3 py-sc-2 text-body-sm text-success-700"
          role="status"
          data-testid="pre-analise-sucesso"
        >
          <p>{sucessoGlobal}</p>
        </div>
      ) : null}
      {/* Irmão da faixa, não filho: o que ficou sem consulta não herda o tom
          de sucesso (E91.F3.C02). */}
      {sucessoGlobal !== null && consultas !== null ? (
        <ConsultasDoProcessamento consultas={consultas} />
      ) : null}
      {caso === null && erroGlobal === null ? (
        <p className="mt-sc-6 text-body-md text-fg-muted" data-testid="pre-analise-loading">
          {t.loading}
        </p>
      ) : null}
      {caso !== null && caso.pecas.length === 0 ? (
        <p className="mt-sc-6 text-body-md text-fg-muted" data-testid="pre-analise-needs-contested">
          {t.semCcbs}
        </p>
      ) : null}
    </>
  );
}

/** O que a seção das peças lê do caso: a régua vigente (E84.F9) e as peças paradas pela custódia
 * (E136.F3). Sem a régua, a prop fica de fora — a tabela trata a ausência como "sem comparação". */
export function dadosDaSecao(
  caso: ExameEletronicoCasoData | null | undefined,
): Pick<SecaoDePecasProps, 'parametrosVersaoCorrente' | 'paradas' | 'ccb'> {
  const paradas = paradasDoCaso(caso);
  const versao = caso?.parametros_versao_corrente;
  // E177.F6 (GUS-253): a CCB do caso aberto por CCB. A divergência mantida na abertura aparece uma
  // vez só, no Exame (E203.F1.T03).
  const porCcb = { ccb: caso?.ccb_do_caso ?? null };
  return versao === undefined
    ? { paradas, ...porCcb }
    : { paradas, parametrosVersaoCorrente: versao, ...porCcb };
}

export type SecaoDePecasProps = TabelaDePecasProps & {
  /** E136.F3 (GUS-152): os documentos fora do exame pela custódia, acima da tabela. */
  paradas?: readonly ObjetoBloqueadoData[];
  ccb?: CcbDaMesaData | null;
};

/**
 * A seção das peças: o resumo do confronto por estado e a tabela.
 *
 * E91.F2 (GUS-51 RF04): o resumo passou a contar pelos MESMOS cinco estados da
 * coluna "Hash com o protocolo" — '3 documentos · 2 hashes conferem · 1 sem
 * referência' —, em vez de "verificados" e "divergentes", que misturavam
 * processamento com resultado.
 */
export function SecaoDePecas({ paradas = [], ccb, ...tabela }: SecaoDePecasProps): JSX.Element {
  const { pecas } = tabela;
  const resumo = resumoDoConfronto(
    contagemDoConfronto(pecas, tabela.emAtualizacao === true),
    ROTULOS_DO_RESUMO,
  );
  return (
    <Blueprint as="section" className="mt-sc-6 space-y-sc-4 p-sc-6" data-testid="pre-analise-lista">
      <header>
        <h2 className="text-display-sm text-fg">{tp.titulo}</h2>
        <p className="mt-1 text-body-sm text-fg-muted" data-testid="pecas-resumo">
          {resumo}
        </p>
        <CcbDoCasoNaMesa ccb={ccb} />
        {/* E89.F2 (RF05): o único selo que anuncia — a seção do quadro repete a
            marca sem região viva, para o leitor de tela não ouvir duas vezes. */}
        {tabela.emAtualizacao === true ? (
          <SeloEmAtualizacao testId="pecas-em-atualizacao" anunciar />
        ) : null}
      </header>
      <PecasParadasPelaCustodia paradas={paradas} pecas={pecas} />
      <TabelaDePecas {...tabela} />
    </Blueprint>
  );
}

/**
 * A falha do processamento automático, com a nova tentativa (E89.F2, RN06).
 *
 * Aviso próprio, e não a faixa de erro das ações: a nova tentativa chama o
 * MESMO automático — o servidor segue decidindo o que verificar e se paga a
 * consulta —, e o botão só existe para quem leu a falha. A tela mostra o último
 * resultado gravado, porque a releitura acontece antes deste aviso aparecer.
 */
export function FalhaDoProcessamento({
  mensagem,
  ocupado,
  onTentarDeNovo,
}: {
  mensagem: string | null;
  ocupado: boolean;
  onTentarDeNovo: () => void;
}): JSX.Element | null {
  if (mensagem === null) return null;
  return (
    <div
      className="mt-sc-4 flex flex-wrap items-start justify-between gap-sc-3 border border-danger bg-danger-50 px-sc-3 py-sc-2"
      role="alert"
      data-testid="processamento-falha"
    >
      <div className="text-body-sm text-danger-700">
        <p className="font-semibold">{ta.falhaTitulo}</p>
        <p className="mt-1">
          {mensagem} {ta.falhaUltimoGravado}
        </p>
      </div>
      <Button
        type="button"
        variant="secondary"
        disabled={ocupado}
        onClick={onTentarDeNovo}
        data-testid="processamento-tentar-de-novo"
      >
        {ta.tentarDeNovo}
      </Button>
    </div>
  );
}
